<template>
  <div id="brain3d-interactive" ref="brainContainer" class="brain3d-interactive">
    <!-- Loading indicator -->
    <div class="loading" v-if="isLoading">
      <div class="loading-text">Loading Brain...</div>
      <div class="loading-progress">
        <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
      </div>
    </div>
    
    <!-- 信息显示面板 -->
    <div class="info-panel" v-if="hoveredInfo.visible">
      <div class="info-content">
        <h4>{{ hoveredInfo.title }}</h4>
        <p>{{ hoveredInfo.description }}</p>
        <div class="info-stats">
          <span>粒子数量: {{ hoveredInfo.particleCount }}</span>
          <span>区域: {{ hoveredInfo.region }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { gsap } from 'gsap'

// 大脑区域信息
const brainRegions = {
  frontal: {
    title: '额叶',
    description: '负责执行功能、决策制定和个性表达',
    particleCount: 0,
    region: '前脑'
  },
  parietal: {
    title: '顶叶',
    description: '处理感觉信息和空间意识',
    particleCount: 0,
    region: '中脑'
  },
  temporal: {
    title: '颞叶',
    description: '处理听觉信息和记忆形成',
    particleCount: 0,
    region: '侧脑'
  },
  occipital: {
    title: '枕叶',
    description: '处理视觉信息',
    particleCount: 0,
    region: '后脑'
  }
}

export default {
  name: 'Brain3DInteractive',
  props: {
    cameraDistance: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      // Three.js 核心对象
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      clock: new THREE.Clock(),
      
      // 大脑相关对象
      brainModel: null,
      particlesSystem: null,
      xRayMeshes: [],
      
      // 光照
      ambientLight: null,
      spotLight: null,
      
      // 动画和状态
      animationId: null,
      isLoading: true,
      loadingProgress: 0,
      
      // 交互相关
      mouse: new THREE.Vector2(),
      raycaster: new THREE.Raycaster(),
      hoveredParticle: null,
      selectedParticle: null,
      
      // UI相关
      hoveredInfo: {
        visible: false,
        title: '',
        description: '',
        particleCount: 0,
        region: ''
      },
      
      // 动画相关
      deltaTime: 0,
      particlesColor: new THREE.Color(0x4a90e2)
    }
  },
  watch: {
    cameraDistance (newVal) {
      if (this.camera && newVal > 0) {
        const currentDistance = this.camera.position.length()
        // 避免不必要的更新
        if (Math.abs(currentDistance - newVal) < 0.1) return

        const scale = newVal / currentDistance
        const newPos = this.camera.position.clone().multiplyScalar(scale)
        // 移除gsap动画，直接更新位置以实现实时联动
        this.camera.position.copy(newPos)
      }
    }
  },
  mounted() {
    console.log('Brain3DInteractive component mounted')
    this.$nextTick(() => {
      setTimeout(() => {
        this.initThreeJS()
        this.addLighting()
        this.loadBrainModel()
        this.startAnimation()
        
        // 添加交互事件监听
        this.addEventListeners()
      }, 100)
    })
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    initThreeJS() {
      console.log('Initializing ThreeJS...')
      
      // 创建场景
      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0x0B151C) // 设置单一的深色背景
      // this.scene.fog = new THREE.Fog(0x313e4b, 300, 1300)

      // 创建相机
      const containerWidth = this.$refs.brainContainer.clientWidth || window.innerWidth
      const containerHeight = this.$refs.brainContainer.clientHeight || window.innerHeight
      
      this.camera = new THREE.PerspectiveCamera(
        50,
        containerWidth / containerHeight,
        1,
        1000
      )
      this.camera.position.z = 20 // 修改初始位置为更近的距离，让大脑显得满屏

      // 创建渲染器
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      })
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(containerWidth, containerHeight)
      // this.renderer.setClearColor(0x313e4b, 1)
      
      // 启用阴影
      this.renderer.shadowMap.enabled = true
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
      
      this.$refs.brainContainer.appendChild(this.renderer.domElement)
      
      // 确保canvas能接收鼠标事件
      this.renderer.domElement.style.pointerEvents = 'auto'
      this.renderer.domElement.style.position = 'relative'
      this.renderer.domElement.style.zIndex = '10'
      
      // 添加控制器
      this.initControls()
      
      // 添加地面
      // this.addFloor()
      
      console.log('ThreeJS initialized successfully')
    },

    initControls() {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.controls.enableDamping = true
      this.controls.dampingFactor = 0.25
      this.controls.enableZoom = true
      this.controls.zoomSpeed = 0.1
      this.controls.panSpeed = 0.3
      this.controls.minDistance = 20
      this.controls.maxDistance = 300 // 恢复原始的最大距离
      this.controls.maxPolarAngle = Math.PI / 2
      this.controls.autoRotate = true
      this.controls.autoRotateSpeed = 0.2
      this.controls.rotateSpeed = 0.1
      this.controls.screenSpacePanning = true // 恢复原始的设置
    },

    addFloor() {
      // 创建地面几何体
      const geometry = new THREE.PlaneGeometry(20000, 20000)
      
      // 创建地面材质 - z=0 地面颜色
      const material = new THREE.MeshPhongMaterial({
        color: 0x111111, // #dde3e9 地面颜色
        opacity: 0.8,
        transparent: true,
      })
      
      // 创建地面网格
      this.plane = new THREE.Mesh(geometry, material)
      this.plane.receiveShadow = true
      this.plane.position.y = -160 // z=0 位置
      this.plane.rotation.x = -0.5 * Math.PI // 让地面水平
      
      // 添加到场景
      this.scene.add(this.plane)
      
      console.log('Floor added with color #dde3e9')
    },

    addLighting() {
      // 环境光
      this.ambientLight = new THREE.AmbientLight(0x404040, 0.5)
      this.scene.add(this.ambientLight)

      // 聚光灯 - 照亮地面和大脑
      // this.spotLight = new THREE.SpotLight(
      //   0x888888,
      //   1.0,
      //   175,
      //   Math.PI / 2,
      //   0.0,
      //   0.0
      // )
      // this.spotLight.position.set(0, 500, -10)
      // this.spotLight.castShadow = true
      // this.scene.add(this.spotLight)
    },

    loadBrainModel() {
      console.log('Loading brain model...')
      this.loadingProgress = 10
      
      const loader = new OBJLoader()
      
      loader.load(
        '/static/models/brain_vertex_low.obj',
        (object) => {
          console.log('Brain model loaded successfully')
          this.loadingProgress = 70
          this.processBrainModel(object)
          this.loadingProgress = 100
          this.isLoading = false
          
          // 开始介绍动画
          setTimeout(() => {
            this.startIntro()
          }, 1000)
        },
        (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 60) + 10
          this.loadingProgress = Math.min(percent, 70)
        },
        (error) => {
          console.error('Failed to load brain model:', error)
          this.createFallbackBrain()
          this.isLoading = false
        }
      )
    },
    
    processBrainModel(brainModel) {
      console.log('Processing brain model...')
      
      this.brainModel = brainModel
      const positions = []
      
      // 处理模型的每个部分
      brainModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const positionAttribute = child.geometry.attributes.position
          if (positionAttribute) {
            for (let i = 0; i < positionAttribute.count; i++) {
              positions.push(
                positionAttribute.getX(i),
                positionAttribute.getY(i),
                positionAttribute.getZ(i)
              )
            }
          }
          // 隐藏原始网格
          child.visible = false
        }
      })

      // 添加到场景
      this.scene.add(brainModel)
      
      // 创建粒子系统
      this.createParticleSystem(positions)
      
      // 创建X射线效果（默认开启，修复黑色问题）
      this.createXRayEffect()
    },

    createParticleSystem(positions) {
      if (!positions.length) return
      
      console.log('Creating particle system...')
      
      const particleCount = positions.length / 3
      const geometry = new THREE.BufferGeometry()
      
      // 减少粒子数量提高性能
      const reducedPositions = []
      const reducedColors = []
      const step = Math.max(1, Math.floor(particleCount / 2000)) // 最多2000个粒子
      
      for (let i = 0; i < positions.length; i += step * 3) {
        reducedPositions.push(positions[i], positions[i + 1], positions[i + 2])
        
        // 蓝色粒子
        reducedColors.push(0.3, 0.6, 1.0)
      }
      
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(reducedPositions), 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(reducedColors), 3))
      
      // 创建粒子材质
      const material = new THREE.PointsMaterial({
        size: 2.0,
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        sizeAttenuation: true
      })
      
      // 创建粒子系统
      this.particlesSystem = new THREE.Points(geometry, material)
      this.scene.add(this.particlesSystem)
      
      console.log('Particle system created with', reducedPositions.length / 3, 'particles')
    },

    createXRayEffect() {
      if (!this.brainModel) return
      
      console.log('Creating X-Ray effect...')
      
      // 创建简化的X射线材质（避免黑色渲染问题）
      const xRayMaterial = new THREE.MeshBasicMaterial({
        color: 0x00aaff,
        transparent: true,
        opacity: 0.2,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
      
      // 为每个网格创建X射线版本
      this.brainModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const xRayMesh = new THREE.Mesh(child.geometry, xRayMaterial.clone())
          xRayMesh.position.copy(child.position)
          xRayMesh.rotation.copy(child.rotation)
          xRayMesh.scale.copy(child.scale)
          this.scene.add(xRayMesh)
          this.xRayMeshes.push(xRayMesh)
        }
      })
      
      console.log('X-Ray effect created')
    },

    createFallbackBrain() {
      console.log('Creating fallback brain...')
      
      // 创建简单的球形粒子系统作为后备
      const geometry = new THREE.BufferGeometry()
      const positions = []
      const colors = []
      
      const particleCount = 3000
      for (let i = 0; i < particleCount; i++) {
        const radius = Math.random() * 100 + 50
        const theta = Math.random() * Math.PI * 2
        const phi = Math.random() * Math.PI
        
        positions.push(
          radius * Math.sin(phi) * Math.cos(theta),
          radius * Math.sin(phi) * Math.sin(theta),
          radius * Math.cos(phi)
        )
        
        colors.push(0.3, 0.6, 1.0)
      }
      
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3))
      
      const material = new THREE.PointsMaterial({
        size: 2.0,
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.8
      })
      
      this.particlesSystem = new THREE.Points(geometry, material)
      this.scene.add(this.particlesSystem)
    },

    startIntro() {
      console.log('Starting intro animation...')
      
      // 相机动画 - 从满屏开始到当前大小，动画更慢
      gsap.fromTo(this.camera.position, 
        { z: 50 }, // 从更近处开始（满屏效果）
        {
          z: 150, // 调整结束位置，保持合适的显示大小
          duration: 8, // 增加动画时长，让动画更慢
          ease: "power2.inOut", // 使用平缓的缓动函数
          onComplete: () => {
            // 启用控制器
            this.controls.enabled = true
          }
        }
      )
    },

    addEventListeners() {
      console.log('Adding event listeners...')
      console.log('Renderer DOM element:', this.renderer.domElement)
      
      // 窗口大小调整
      window.addEventListener('resize', this.onWindowResize)
      
      // 鼠标事件
      this.renderer.domElement.addEventListener('mousemove', this.onMouseMove)
      this.renderer.domElement.addEventListener('click', this.onMouseClick)
      
      console.log('Mouse event listeners added to renderer DOM element')
      
      // 键盘事件
      window.addEventListener('keydown', this.onKeyDown)
      
      // 初始化射线投射器
      this.raycaster = new THREE.Raycaster()
      this.raycaster.params.Points.threshold = 10
      
      console.log('All event listeners added successfully')
    },

    onWindowResize() {
      if (!this.camera || !this.renderer) return
      
      const width = this.$refs.brainContainer.clientWidth || window.innerWidth
      const height = this.$refs.brainContainer.clientHeight || window.innerHeight
      
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()
      
      this.renderer.setSize(width, height)
    },

    onMouseMove(event) {
      // 更新鼠标位置用于射线检测
      const rect = this.$refs.brainContainer.getBoundingClientRect()
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
      
      // 简化的调试信息
      if (Math.random() < 0.01) { // 只输出1%的mousemove事件，避免控制台被刷屏
        console.log('Mouse move detected, position:', this.mouse.x.toFixed(2), this.mouse.y.toFixed(2))
      }
    },

    onMouseClick(event) {
      console.log('Mouse click detected!', event)
      
      if (!this.particlesSystem) {
        console.log('Particles system not ready')
        return
      }
      
      // 射线检测
      this.raycaster.setFromCamera(this.mouse, this.camera)
      const intersects = this.raycaster.intersectObject(this.particlesSystem)
      
      console.log('Raycaster intersects:', intersects.length)
      
      if (intersects.length > 0) {
        console.log('Particle clicked!', intersects[0])
        this.selectParticle(intersects[0])
        this.showParticleInfo(intersects[0])
      } else {
        console.log('No particle clicked')
        this.clearSelection()
      }
    },

    onKeyDown(event) {
      switch(event.key.toLowerCase()) {
        case ' ': // 空格键 - 暂停/继续动画
          event.preventDefault()
          this.toggleAnimation()
          break
        case 'r': // R键 - 重置视角
          this.resetCamera()
          break
        case 'c': // C键 - 切换粒子颜色
          this.changeParticleColor()
          break
      }
    },

    selectParticle(intersection) {
      this.selectedParticle = intersection
      this.createHighlightEffect(intersection.point)
    },

    createHighlightEffect(position) {
      // 创建高亮圆环
      const geometry = new THREE.RingGeometry(15, 25, 16)
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide
      })
      
      const highlight = new THREE.Mesh(geometry, material)
      highlight.position.copy(position)
      highlight.lookAt(this.camera.position)
      this.scene.add(highlight)
      
      // 动画效果
      gsap.fromTo(highlight.scale, 
        { x: 0, y: 0, z: 0 },
        { 
          x: 1, y: 1, z: 1,
          duration: 0.3,
          ease: "back.out(1.7)"
        }
      )
      
      // 2秒后移除
      setTimeout(() => {
        gsap.to(highlight.material, {
          opacity: 0,
          duration: 0.5,
          onComplete: () => {
            this.scene.remove(highlight)
            geometry.dispose()
            material.dispose()
          }
        })
      }, 2000)
    },

    showParticleInfo(intersection) {
      const point = intersection.point
      const region = this.getBrainRegion(point)
      
      this.hoveredInfo = {
        visible: true,
        title: region.title,
        description: region.description,
        particleCount: Math.floor(Math.random() * 1000) + 500,
        region: region.region
      }
      
      // 3秒后自动隐藏
      setTimeout(() => {
        this.hoveredInfo.visible = false
      }, 3000)
    },

    getBrainRegion(point) {
      // 根据3D位置判断大脑区域
      if (point.z > 0) {
        return brainRegions.frontal
      } else if (point.x > 0) {
        return brainRegions.parietal
      } else if (point.y < 0) {
        return brainRegions.temporal
      } else {
        return brainRegions.occipital
      }
    },

    clearSelection() {
      this.selectedParticle = null
      this.hoveredInfo.visible = false
    },

    toggleAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      } else {
        this.animate()
      }
    },

    resetCamera() {
      gsap.to(this.camera.position, {
        x: 0,
        y: 0,
        z: 380,
        duration: 2,
        ease: "power2.inOut"
      })
    },

    changeParticleColor() {
      if (!this.particlesSystem) return
      
      const colors = this.particlesSystem.geometry.attributes.color.array
      
      // 随机改变颜色
      for (let i = 0; i < colors.length; i += 3) {
        colors[i] = Math.random() * 0.5 + 0.5
        colors[i + 1] = Math.random() * 0.5 + 0.5
        colors[i + 2] = 1.0
      }
      
      this.particlesSystem.geometry.attributes.color.needsUpdate = true
    },

    startAnimation() {
      this.animate()
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate.bind(this))
      
      this.deltaTime = this.clock.getDelta()
      
      // 更新控制器
      if (this.controls) {
        // 强制保持自动旋转，即使用户交互后也继续旋转
        this.controls.autoRotate = true
        this.controls.update()
      }
      
      // 更新X射线效果动画
      if (this.xRayMeshes.length > 0) {
        const time = this.clock.elapsedTime
        this.xRayMeshes.forEach(mesh => {
          mesh.material.opacity = 0.15 + Math.sin(time * 2) * 0.1
        })
      }
      
      // 更新粒子动画
      if (this.particlesSystem) {
        const time = this.clock.elapsedTime
        this.particlesSystem.material.opacity = 0.7 + Math.sin(time * 2) * 0.2
        this.particlesSystem.rotation.y += 0.002
      }
      
      // 渲染
      this.renderer.render(this.scene, this.camera)
    },

    cleanup() {
      console.log('Cleaning up Brain3DInteractive...')
      
      // 清理动画
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
      }
      
      // 清理事件监听器
      window.removeEventListener('resize', this.onWindowResize)
      window.removeEventListener('keydown', this.onKeyDown)
      
      // 清理Three.js资源
      if (this.renderer) {
        this.renderer.dispose()
      }
      
      if (this.scene) {
        this.scene.clear()
      }
    }
  }
}
</script>

<style scoped>
.brain3d-interactive {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0;
  padding: 0;
  pointer-events: auto; /* 确保组件本身能接收鼠标事件 */
}

/* 确保Three.js canvas能接收所有鼠标事件 */
.brain3d-interactive canvas {
  pointer-events: auto !important;
  position: relative;
  z-index: 1;
}

/* 信息面板样式 */
.info-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  pointer-events: none;
}

.info-content {
  background: rgba(0, 0, 0, 0.9);
  border-radius: 12px;
  padding: 20px;
  color: white;
  font-family: 'Arial', sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 300px;
  animation: slideInRight 0.3s ease-out;
}

.info-content h4 {
  margin: 0 0 10px 0;
  color: #4a90e2;
  font-size: 20px;
  font-weight: bold;
}

.info-content p {
  margin: 0 0 15px 0;
  line-height: 1.5;
  color: #ccc;
}

.info-stats {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 10px;
}

.info-stats span {
  font-size: 12px;
  color: #999;
}

/* Loading样式 */
.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  text-align: center;
  color: white;
}

.loading-text {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #4a90e2;
  text-shadow: 0 0 10px rgba(74, 144, 226, 0.5);
}

.loading-progress {
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4a90e2, #764ba2);
  border-radius: 2px;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(74, 144, 226, 0.5);
}

/* 动画效果 */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style> 